// Полетата на „Схема на заведението" по дизайна — общи за инспектора вдясно и
// за модалите: етикет (eyebrow), текстово поле, брой места (стъпков брояч +
// бързи бутони), плочки за форма на маса, сегменти. Само Tailwind; кехлибареният
// focus ring заменя синия на antd.

import { useRef } from "react";
import { useTranslation } from "react-i18next";
import type { TableShape } from "../../../../api/restaurantSchema";
import { TABLE_SHAPES } from "../../../../components/restaurant-schema/elementCatalog";
import { useFocusOnOpen } from "../../../../hooks/useFocusOnOpen";

export const FieldLabel = ({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <div className="text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1px] text-muted">
    {children}
    {required && <span className="ml-1 text-danger-ink">*</span>}
  </div>
);

export const Field = ({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  // Грешка в полето — показва се вместо подсказката.
  error?: string | null;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-[7px]">
    <FieldLabel required={required}>{label}</FieldLabel>
    {children}
    {error ? (
      <div role="alert" className="text-[12px] leading-[1.45] text-danger-ink">
        {error}
      </div>
    ) : (
      hint && <div className="text-[12px] leading-[1.45] text-muted">{hint}</div>
    )}
  </div>
);

export const TextField = ({
  value,
  onChange,
  onCommit,
  onEnter,
  placeholder,
  maxLength,
  autoFocus,
  compact,
}: {
  value: string;
  onChange: (value: string) => void;
  // Enter / напускане на полето — инспекторът записва чак тогава, не на всеки знак.
  onCommit?: () => void;
  // Само Enter — модалите изпращат формата, но не и при напускане на полето.
  onEnter?: () => void;
  placeholder?: string;
  maxLength?: number;
  // Фокус при монтиране (отваряне на модала) — не с родния autoFocus: в antd
  // Modal порталът още не е в DOM-а и фокусът отива на диалога (useFocusOnOpen).
  autoFocus?: boolean;
  compact?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useFocusOnOpen(!!autoFocus, inputRef);
  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onCommit}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        const submit = onEnter ?? onCommit;
        if (!submit) return;
        event.preventDefault();
        submit();
      }}
      placeholder={placeholder}
      maxLength={maxLength}
      // 16px на телефон — под това iOS Safari зуумва страницата при фокус.
      className={`w-full border border-shade-3 bg-surface px-3 text-[16px] text-ink outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-faint focus:border-brand focus:shadow-[0_0_0_3px_rgba(217,115,13,0.15)] sm:text-[14px] ${
        compact ? "h-[38px] rounded-[9px]" : "h-10 rounded-[10px]"
      }`}
    />
  );
};

/** Брой места: − N + и бързите бутони (избраният е плътен тъмен). */
export const SeatsField = ({
  value,
  min,
  max,
  chips,
  onChange,
  compact,
}: {
  value: number;
  min: number;
  max: number;
  chips: number[];
  onChange: (value: number) => void;
  compact?: boolean;
}) => {
  const stepButton = `flex cursor-pointer items-center justify-center border-none bg-transparent text-[18px] text-ink-2 transition-colors duration-150 hover:bg-fill disabled:cursor-not-allowed disabled:opacity-35 ${
    compact ? "h-[38px] w-9" : "h-10 w-10"
  }`;
  return (
    <div className={`flex gap-2 ${compact ? "items-center" : "flex-col"}`}>
      <div
        className={`flex w-fit items-center overflow-hidden border border-shade-3 ${
          compact ? "h-[38px] rounded-[9px]" : "h-10 rounded-[10px]"
        }`}
      >
        <button
          type="button"
          aria-label="−"
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
          className={stepButton}
        >
          −
        </button>
        <span
          className={`text-center text-[15px] font-bold tabular-nums text-ink ${
            compact ? "w-[34px]" : "w-10"
          }`}
        >
          {value}
        </span>
        <button
          type="button"
          aria-label="+"
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
          className={stepButton}
        >
          +
        </button>
      </div>
      <div className="flex gap-1">
        {chips.map((count) => {
          const selected = count === value;
          return (
            <button
              key={count}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(count)}
              className={`w-[34px] cursor-pointer rounded-[8px] border font-semibold tabular-nums transition-colors duration-150 ${
                compact ? "h-[34px] text-[13px]" : "h-[30px] text-[12.5px]"
              } ${
                selected
                  ? "border-ink bg-ink text-surface"
                  : "border-shade-3 bg-surface text-ink-2 hover:bg-fill"
              }`}
            >
              {count}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Мострата на формата в плочката — плот отгоре.
const SHAPE_SWATCH: Record<TableShape, { compact: string; regular: string }> = {
  round: { compact: "h-5 w-5 rounded-full", regular: "h-6 w-6 rounded-full" },
  square: { compact: "h-5 w-5 rounded-[5px]", regular: "h-6 w-6 rounded-[6px]" },
  rectangle: {
    compact: "h-[18px] w-[30px] rounded-[5px]",
    regular: "h-[22px] w-[38px] rounded-[6px]",
  },
};

/** Кръгла / Квадратна / Правоъгълна — избраната е в кехлибарено. */
export const TableShapeTiles = ({
  value,
  onChange,
  compact,
}: {
  value: TableShape;
  onChange: (shape: TableShape) => void;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className={`grid grid-cols-3 ${compact ? "gap-1.5" : "gap-2"}`}>
      {TABLE_SHAPES.map((shape) => {
        const selected = shape === value;
        return (
          <button
            key={shape}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(shape)}
            className={`flex cursor-pointer flex-col items-center justify-center whitespace-nowrap border-[1.5px] font-semibold transition-[border-color,background,color] duration-150 ${
              compact
                ? "h-[58px] gap-[5px] rounded-[10px] text-[11.5px]"
                : "h-[72px] gap-2 rounded-[12px] text-[13px]"
            } ${
              selected
                ? "border-brand bg-brand-tint text-brand-deep-ink"
                : "border-shade-3 bg-surface text-ink-2 hover:border-shade-4"
            }`}
          >
            <span
              className={`border-2 border-current ${SHAPE_SWATCH[shape][compact ? "compact" : "regular"]}`}
            />
            {t(`restaurantSchema.tableShapes.${shape}`)}
          </button>
        );
      })}
    </div>
  );
};

export function Segmented<Value extends string | number>({
  options,
  value,
  onChange,
}: {
  options: { value: Value; label: string }[];
  value: Value;
  onChange: (value: Value) => void;
}) {
  return (
    <div className="grid auto-cols-fr grid-flow-col gap-0.5 rounded-[10px] bg-fill-2 p-[3px]">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={`h-8 cursor-pointer whitespace-nowrap rounded-[8px] border-none px-2 text-[13px] font-semibold transition-[background,color] duration-150 ${
              selected
                ? "bg-surface text-ink shadow-[0_1px_2px_rgba(28,25,23,0.12)]"
                : "bg-transparent text-body hover:text-ink"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
